import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BankAccount, Batch, Instruction, Vendor, Voucher } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Batches() {
  const { persona } = useAuth();
  const [showBuild, setShowBuild] = useState(false);
  const [failTarget, setFailTarget] = useState<Instruction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQ = useApiQuery<{ items: Batch[] }>('/api/v1/payments/batches?limit=100');
  const accountsQ = useApiQuery<{ items: BankAccount[] }>('/api/v1/payments/accounts');

  const canBuild = persona?.roles.includes('TREASURY_PAYMENTS_OFFICER') ?? false;
  const canReleaseA = persona?.roles.includes('TREASURY_RELEASE_A') ?? false;
  const canReleaseB = persona?.roles.includes('TREASURY_RELEASE_B') ?? false;
  const canCash = persona?.roles.includes('MOF_CASH_MANAGER') ?? false;

  const release = async (b: Batch) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/payments/batches/${b.id}/release`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Release failed');
    }
  };

  const acknowledge = async (b: Batch) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/payments/batches/${b.id}/acknowledge`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Acknowledge failed');
    }
  };

  const accountName = (id: string) => accountsQ.data?.items.find((a) => a.id === id)?.name ?? id;

  return (
    <div>
      <PageHeader
        title="Payment batches — Central Bank release"
        subtitle="Dual control: Officer A approves, Officer B (a different person) transmits the ISO 20022 file to CBS. The file hash is anchored to the audit chain before transmission."
        actions={
          canBuild ? (
            <button className="btn-primary" onClick={() => setShowBuild(true)}>
              + Build batch from scheduled vouchers
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="space-y-4">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <div className="card">
            <EmptyState title="No payment batches" hint="Schedule vouchers first, then assemble them into a batch." />
          </div>
        ) : (
          listQ.data!.items.map((b) => (
            <div key={b.id} className="card p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{b.number}</span>
                    <StatusBadge status={b.status} />
                    <span className="text-xs text-slate-500">
                      {b.channel} · value date {formatDate(b.valueDate)} · from {accountName(b.debtorAccountId)}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">
                    {b.approvedA ? `Approved A: ${b.approvedA}` : 'Awaiting first approval'}
                    {b.approvedB ? ` · Approved B: ${b.approvedB}` : b.approvedA ? ' · awaiting second approval' : ''}
                    {b.fileHash ? ` · file ${b.fileRef} (sha256 ${b.fileHash.slice(0, 12)}…)` : ''}
                  </div>
                </div>
                <div className="flex gap-2">
                  {canReleaseA && b.status === 'ASSEMBLED' ? (
                    <button className="btn-success" onClick={() => release(b)}>
                      Approve (Officer A)
                    </button>
                  ) : null}
                  {canReleaseB && b.status === 'APPROVED_1' ? (
                    <button className="btn-primary" onClick={() => release(b)}>
                      Approve & transmit (Officer B)
                    </button>
                  ) : null}
                  {canCash && b.status === 'TRANSMITTED' ? (
                    <button className="btn-secondary" onClick={() => acknowledge(b)}>
                      Mark acknowledged by CBS
                    </button>
                  ) : null}
                </div>
              </div>
              <table className="mt-3 w-full">
                <thead>
                  <tr>
                    <th className="table-head">Beneficiary</th>
                    <th className="table-head">Bank / account</th>
                    <th className="table-head">End-to-end ref</th>
                    <th className="table-head">Source</th>
                    <th className="table-head text-right">Amount</th>
                    <th className="table-head">Status</th>
                    {canCash ? <th className="table-head" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {(b.instructions ?? []).map((ins) => (
                    <tr key={ins.id}>
                      <td className="table-cell">{ins.beneficiaryName}</td>
                      <td className="table-cell text-xs tabular-nums">
                        {ins.bankCode} · {ins.accountNo}
                      </td>
                      <td className="table-cell text-xs">{ins.endToEndRef}</td>
                      <td className="table-cell text-xs">{ins.sourceType}</td>
                      <td className="table-cell text-right tabular-nums">{scr(ins.amount)}</td>
                      <td className="table-cell">
                        <StatusBadge status={ins.status} />
                        {ins.returnReason ? <div className="text-[11px] text-rose-600">{ins.returnReason}</div> : null}
                      </td>
                      {canCash ? (
                        <td className="table-cell text-right">
                          {ins.status === 'TRANSMITTED' ? (
                            <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setFailTarget(ins)}>
                              Mark failed
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      {showBuild ? (
        <BuildModal
          accounts={accountsQ.data?.items ?? []}
          onClose={() => setShowBuild(false)}
          onDone={() => {
            setShowBuild(false);
            listQ.reload();
          }}
        />
      ) : null}
      {failTarget ? (
        <FailModal
          instruction={failTarget}
          onClose={() => setFailTarget(null)}
          onDone={() => {
            setFailTarget(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function BuildModal({ accounts, onClose, onDone }: { accounts: BankAccount[]; onClose: () => void; onDone: () => void }) {
  const vouchersQ = useApiQuery<{ items: Voucher[] }>('/api/v1/expenditure/vouchers?status=SCHEDULED&limit=100');
  const batchesQ = useApiQuery<{ items: Batch[] }>('/api/v1/payments/batches?limit=100');
  const vendorsQ = useApiQuery<{ items: Vendor[] }>('/api/v1/commitments/vendors?limit=100');

  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [channel, setChannel] = useState<'EFT' | 'SWIFT' | 'CHEQUE_LEGACY'>('EFT');
  const [debtorAccountId, setDebtorAccountId] = useState(accounts.find((a) => a.holder === 'TSA')?.id ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bankCodes, setBankCodes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Vouchers already carried by an open batch shouldn't be paid twice.
  const alreadyBatched = useMemo(() => {
    const s = new Set<string>();
    for (const b of batchesQ.data?.items ?? []) {
      for (const i of b.instructions ?? []) {
        if (i.sourceType === 'VOUCHER' && i.status !== 'FAILED') s.add(i.sourceRef);
      }
    }
    return s;
  }, [batchesQ.data]);

  const vendorOf = (v: Voucher): Vendor | undefined => {
    const vendorId = v.invoices?.[0]?.vendorId;
    return (vendorsQ.data?.items ?? []).find((x) => x.id === vendorId);
  };

  const payable = (vouchersQ.data?.items ?? []).filter((v) => !alreadyBatched.has(v.id));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const instructions = payable
        .filter((v) => selected.has(v.id))
        .map((v) => {
          const vendor = vendorOf(v);
          return {
            sourceType: 'VOUCHER' as const,
            sourceRef: v.id,
            beneficiaryName: vendor?.name ?? v.number,
            bankCode: bankCodes[v.id] ?? 'SEYLSCSC',
            accountNo: vendor?.bankAccount ?? '00000000',
            amount: Number.parseFloat(v.amount),
          };
        });
      await api.post('/api/v1/payments/batches', { valueDate, channel, debtorAccountId, instructions });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to build batch');
    } finally {
      setBusy(false);
    }
  };

  const total = payable.filter((v) => selected.has(v.id)).reduce((s, v) => s + Number.parseFloat(v.amount), 0);

  return (
    <Modal title="Build payment batch" onClose={onClose} wide>
      <div className="space-y-4">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Value date">
            <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
          </Field>
          <Field label="Channel">
            <select className="field-input" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
              <option value="EFT">EFT (domestic)</option>
              <option value="SWIFT">SWIFT</option>
              <option value="CHEQUE_LEGACY">Cheque (legacy)</option>
            </select>
          </Field>
          <Field label="Debit account (TSA)">
            <select className="field-input" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)}>
              <option value="">Select account…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.cbsRef})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div>
          <div className="field-label">Scheduled vouchers</div>
          {vouchersQ.loading ? (
            <Spinner />
          ) : payable.length === 0 ? (
            <EmptyState title="No scheduled vouchers awaiting batching" />
          ) : (
            <div className="max-h-60 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {payable.map((v) => {
                const vendor = vendorOf(v);
                return (
                  <div key={v.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selected.has(v.id)}
                      onChange={() => {
                        const next = new Set(selected);
                        if (next.has(v.id)) next.delete(v.id);
                        else next.add(v.id);
                        setSelected(next);
                      }}
                    />
                    <span className="flex-1 text-sm">
                      <span className="font-medium">{v.number}</span> → {vendor?.name ?? 'unknown supplier'}
                      <span className="ml-2 text-xs text-slate-400">acct {vendor?.bankAccount ?? '—'}</span>
                    </span>
                    {selected.has(v.id) ? (
                      <input
                        className="field-input w-32 !py-1 text-xs"
                        value={bankCodes[v.id] ?? 'SEYLSCSC'}
                        onChange={(e) => setBankCodes({ ...bankCodes, [v.id]: e.target.value })}
                        title="Beneficiary bank code"
                      />
                    ) : null}
                    <span className="text-sm tabular-nums">{scr(v.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-2 text-sm text-slate-600">
            {selected.size} instruction(s) · total <strong>{scr(total)}</strong>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || selected.size === 0 || !debtorAccountId}>
            {busy ? 'Assembling…' : `Assemble batch (${selected.size})`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function FailModal({ instruction, onClose, onDone }: { instruction: Instruction; onClose: () => void; onDone: () => void }) {
  const [returnCode, setReturnCode] = useState('AC01');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/payments/instructions/${instruction.id}/fail`, { returnCode, reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Mark instruction failed — ${instruction.endToEndRef}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Return code (ISO 20022)">
          <select className="field-input" value={returnCode} onChange={(e) => setReturnCode(e.target.value)}>
            <option value="AC01">AC01 — incorrect account number</option>
            <option value="AC04">AC04 — account closed</option>
            <option value="AM04">AM04 — insufficient funds</option>
            <option value="RC01">RC01 — invalid bank identifier</option>
          </select>
        </Field>
        <Field label="Reason">
          <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Saving…' : 'Mark failed'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
